import { ALL_ACTIONS } from './Action';

import type { Action } from './Action';
import type { CommunityId } from 'domain/CommunityId';

/**
 * The port.
 *
 * Shaped as `permissions-service`'s `GET /v1/permissions/me` response, plus the scope it was
 * fetched for, so that the adapter producing it can be swapped without touching anything above.
 */

/**
 * Exactly one target per snapshot, matching the endpoint's contract (ADR-0007).
 *
 * `community:setEntitlements` is the awkward one, and the two adapters differ on it. It TARGETS a
 * community, but the rule the app enforces today is organisation-level - `isCompanyAdmin` or
 * organisation admin, with no community in it - so the legacy adapter answers it under either scope.
 * The service cannot: a community-targeted action needs a community-scoped snapshot, so the
 * organisation console will need one for that single check. Open on HNS-349.
 */
export type PermissionsScope =
  | { communityId: CommunityId; organisationId?: never }
  | { organisationId: number; communityId?: never };

/**
 * One place answers come from, and the set of questions it speaks for.
 *
 * A snapshot can be blended from several: the service answers the published catalogue, a local
 * overlay answers what the catalogue cannot express. Asking which source OWNS an action has no
 * answer - `community:setEntitlements` is org-admin-derived in the service AND `isCompanyAdmin`
 * locally - so determinacy is a union across contributors rather than a matter of ownership, and
 * each source declares what it could contribute instead of claiming it.
 */
export type PermissionsSource = {
  /**
   * What this source grants. Read only once the source has settled: a source that is still loading
   * has not answered, and one that could not answer has nothing to say, so neither grants.
   */
  allowed: ReadonlySet<Action>;
  /**
   * Every action this source could grant, whether or not it currently does. Declaring it is what
   * makes a denial determinable: an action no source contributes is a determined no rather than a
   * gate waiting on an answer that can only ever be no.
   */
  contributes: ReadonlySet<Action>;
  /** No answer yet. Gates hold rather than deny and redirect - unless another source grants. */
  isLoading: boolean;
  /**
   * The answer could not be determined. Denies, per the service's "treat unreachable as denied",
   * while staying distinguishable from a determined denial - and, again, unless another source
   * grants, so an outage here cannot take away a capability derived somewhere else.
   */
  isError: boolean;
};

export type PermissionsSnapshot = {
  /**
   * Every source contributing to this answer. Order is not meaningful; `evaluate` unions them.
   * An empty list grants nothing, and grants nothing undetermined.
   */
  sources: readonly PermissionsSource[];
  /**
   * What the sources are about. `undefined` when there is nothing to scope to - a signed-in user
   * with no community - in which case nothing community-scoped can be granted. Required rather than
   * optional so that an adapter has to answer the question. Snapshot-wide rather than per source,
   * because a snapshot has exactly one target (ADR-0007) whoever answered for it.
   */
  scope: PermissionsScope | undefined;
};

/**
 * For an adapter that is the whole answer on its own, as the legacy one is: it derives every action
 * in the vocabulary, so nothing else can be waiting to contribute one.
 */
export const CONTRIBUTES_EVERY_ACTION: ReadonlySet<Action> = new Set(ALL_ACTIONS);
